import React, { useState } from 'react';
import styled from 'styled-components';
import Constants from '../constants/Constants';

const PageContainer = styled.div`
  margin: 0 auto;
  background: #D1CDC8;
  min-height: 100vh;
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (max-width: 768px) {
    padding: 20px 10px;
  }
`;

const Title = styled.h1`
  font-family: 'Roboto', sans-serif;
  font-size: 3rem;
  color: #333;
  margin-bottom: 40px;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 30px;
  }
`;

const Card = styled.div`
  background-color: #E7E6E4;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  max-width: 900px;
  width: 100%;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 30px;
  border-bottom: 2px solid #ccc;
`;

const Tab = styled.button`
  padding: 12px 24px;
  background-color: ${props => props.active ? '#61916e' : 'transparent'};
  color: ${props => props.active ? 'white' : '#333'};
  border: none;
  border-radius: 8px 8px 0 0;
  font-size: 1rem;
  font-family: 'Roboto', sans-serif;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.active ? '#4a7a5a' : '#ddd'};
  }
`;

const FormGroup = styled.div`
  margin-bottom: 25px;
`;

const Label = styled.label`
  display: block;
  font-family: 'Roboto', sans-serif;
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 8px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #ccc;
  border-radius: 8px;
  font-size: 1rem;
  font-family: 'Roboto', sans-serif;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #61916e;
  }
`;

const FileInputWrapper = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
`;

const FileInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #ccc;
  border-radius: 8px;
  font-size: 1rem;
  font-family: 'Roboto', sans-serif;
  background-color: white;
  cursor: pointer;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #61916e;
  }
`;

const Button = styled.button`
  padding: 14px 24px;
  background-color: #61916e;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-family: 'Roboto', sans-serif;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 10px;
  
  &:hover:not(:disabled) {
    background-color: #4a7a5a;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.2);
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const FullWidthButton = styled(Button)`
  width: 100%;
`;

const DeleteButton = styled.button`
  padding: 8px 16px;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-family: 'Roboto', sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background-color: #c82333;
    transform: translateY(-1px);
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const Message = styled.div`
  margin-top: 20px;
  padding: 16px;
  border-radius: 8px;
  font-family: 'Roboto', sans-serif;
  font-size: 1rem;
  line-height: 1.6;
`;

const SuccessMessage = styled(Message)`
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
`;

const ErrorMessage = styled(Message)`
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
`;

const ResultInfo = styled.div`
  margin-top: 20px;
  padding: 20px;
  background-color: white;
  border-radius: 8px;
  border: 1px solid #ddd;
`;

const ResultTitle = styled.h3`
  font-family: 'Roboto', sans-serif;
  font-size: 1.3rem;
  color: #333;
  margin-bottom: 15px;
`;

const ResultItem = styled.div`
  margin-bottom: 10px;
  font-family: 'Roboto', sans-serif;
  font-size: 1rem;
  color: #555;
  
  strong {
    color: #333;
    margin-right: 8px;
  }
`;

const ImagePreview = styled.img`
  max-width: 100%;
  max-height: 300px;
  margin-top: 15px;
  border-radius: 8px;
  border: 2px solid #ddd;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255,255,255,.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  margin-right: 10px;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 20px;
`;

const ImageCard = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ImageThumbnail = styled.img`
  width: 100%;
  height: 150px;
  object-fit: cover;
  border-radius: 6px;
  margin-bottom: 10px;
  border: 2px solid #ddd;
`;

const ImageInfo = styled.div`
  width: 100%;
  font-family: 'Roboto', sans-serif;
  font-size: 0.9rem;
  color: #555;
  margin-bottom: 10px;
  word-break: break-all;
  text-align: center;
`;

const ImageActions = styled.div`
  width: 100%;
  display: flex;
  gap: 10px;
  justify-content: center;
`;

const ViewButton = styled.a`
  padding: 6px 12px;
  background-color: #61916e;
  color: white;
  text-decoration: none;
  border-radius: 6px;
  font-size: 0.85rem;
  font-family: 'Roboto', sans-serif;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #4a7a5a;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
  font-family: 'Roboto', sans-serif;
  font-size: 1.1rem;
`;

function ImageManager() {
  const [activeTab, setActiveTab] = useState('upload');
  
  // Upload state
  const [folderPath, setFolderPath] = useState('');
  const [password, setPassword] = useState('');
  const [filename, setFilename] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  
  // List state
  const [listPath, setListPath] = useState('');
  const [listLoading, setListLoading] = useState(false);
  const [images, setImages] = useState(null);
  const [listError, setListError] = useState(null);
  
  // Delete state
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState({});

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setUploadError(null);
      setUploadResult(null);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!folderPath.trim()) {
      setUploadError('Please enter a folder path (e.g., A0001)');
      return;
    }
    
    if (!password.trim()) {
      setUploadError('Please enter a password');
      return;
    }
    
    if (!selectedFile) {
      setUploadError('Please select an image file');
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('image', selectedFile);
      if (filename.trim()) {
        formData.append('filename', filename.trim());
      }

      const response = await fetch(
        `${Constants.get.SERVER_URL}/upload_img/${folderPath.trim()}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${password.trim()}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(errorData.error || `Upload failed with status ${response.status}`);
      }

      const data = await response.json();
      setUploadResult(data);
      
      // Reset form after successful upload
      setSelectedFile(null);
      setPreviewUrl(null);
      setFilename('');
      e.target.reset();
      
      // Refresh image list if we're viewing the same path
      if (listPath === folderPath.trim()) {
        handleListImages();
      }
      
    } catch (err) {
      setUploadError(err.message || 'An error occurred while uploading the image');
    } finally {
      setUploading(false);
    }
  };

  const handleListImages = async () => {
    if (!listPath.trim()) {
      setListError('Please enter a folder path (e.g., A0001)');
      return;
    }

    setListLoading(true);
    setListError(null);

    try {
      const response = await fetch(
        `${Constants.get.SERVER_URL}/images/${listPath.trim()}`
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to list images' }));
        throw new Error(errorData.error || `Failed to list images with status ${response.status}`);
      }

      const data = await response.json();
      setImages(data);
      
    } catch (err) {
      setListError(err.message || 'An error occurred while listing images');
      setImages(null);
    } finally {
      setListLoading(false);
    }
  };

  const handleDeleteImage = async (imageId, folderPath) => {
    if (!deletePassword.trim()) {
      alert('Please enter a password to delete images');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${imageId}?`)) {
      return;
    }

    setDeleting(prev => ({ ...prev, [imageId]: true }));

    try {
      const response = await fetch(
        `${Constants.get.SERVER_URL}/images/${folderPath}/${imageId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${deletePassword.trim()}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Delete failed' }));
        throw new Error(errorData.error || `Delete failed with status ${response.status}`);
      }

      // Refresh image list
      handleListImages();
      
    } catch (err) {
      alert(err.message || 'An error occurred while deleting the image');
    } finally {
      setDeleting(prev => ({ ...prev, [imageId]: false }));
    }
  };

  return (
    <PageContainer>
      <Title>Image Manager</Title>
      
      <Card>
        <TabContainer>
          <Tab active={activeTab === 'upload'} onClick={() => setActiveTab('upload')}>
            Upload Image
          </Tab>
          <Tab active={activeTab === 'list'} onClick={() => setActiveTab('list')}>
            List Images
          </Tab>
        </TabContainer>

        {activeTab === 'upload' && (
          <form onSubmit={handleUpload}>
            <FormGroup>
              <Label htmlFor="folderPath">Folder Path</Label>
              <Input
                id="folderPath"
                type="text"
                value={folderPath}
                onChange={(e) => setFolderPath(e.target.value)}
                placeholder="e.g., A0001, A0002, A0001/PublicArea, A0001/RoomA"
                disabled={uploading}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                disabled={uploading}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="filename">Filename (Optional)</Label>
              <Input
                id="filename"
                type="text"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                placeholder="e.g., custom-name.jpg (leave empty generate uuid filename)"
                disabled={uploading}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="imageFile">Image File</Label>
              <FileInputWrapper>
                <FileInput
                  id="imageFile"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={uploading}
                />
              </FileInputWrapper>
              {previewUrl && (
                <ImagePreview src={previewUrl} alt="Preview" />
              )}
            </FormGroup>

            <FullWidthButton type="submit" disabled={uploading}>
              {uploading && <LoadingSpinner />}
              {uploading ? 'Uploading...' : 'Upload Image'}
            </FullWidthButton>
          </form>
        )}

        {activeTab === 'list' && (
          <div>
            <FormGroup>
              <Label htmlFor="listPath">Folder Path</Label>
              <Input
                id="listPath"
                type="text"
                value={listPath}
                onChange={(e) => setListPath(e.target.value)}
                placeholder="e.g., A0001, A0002, A0001/PublicArea, A0001/RoomA"
                disabled={listLoading}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="deletePassword">Password (for deletion)</Label>
              <Input
                id="deletePassword"
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Enter password to enable delete"
                disabled={listLoading}
              />
            </FormGroup>

            <FullWidthButton onClick={handleListImages} disabled={listLoading}>
              {listLoading && <LoadingSpinner />}
              {listLoading ? 'Loading...' : 'List Images'}
            </FullWidthButton>

            {listError && (
              <ErrorMessage>
                <strong>Error:</strong> {listError}
              </ErrorMessage>
            )}

            {images && (
              <div>
                <ResultInfo>
                  <ResultTitle>Images in {images.path}</ResultTitle>
                  <ResultItem>
                    <strong>Total Count:</strong> {images.count}
                  </ResultItem>
                </ResultInfo>
                {images.images && images.images.length > 0 ? (
                  <ImageGrid>
                    {images.images.map((image) => (
                      <ImageCard key={image.imageId}>
                        <ImageThumbnail 
                          src={`${Constants.get.SERVER_URL}${image.url}`} 
                          alt={image.imageId}
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="150"%3E%3Crect fill="%23ddd" width="200" height="150"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="14" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EImage not found%3C/text%3E%3C/svg%3E';
                          }}
                        />
                        <ImageInfo>{image.imageId}</ImageInfo>
                        <ImageActions>
                          <ViewButton 
                            href={`${Constants.get.SERVER_URL}${image.url}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            View
                          </ViewButton>
                          <DeleteButton
                            onClick={() => handleDeleteImage(image.imageId, images.path)}
                            disabled={deleting[image.imageId] || !deletePassword.trim()}
                          >
                            {deleting[image.imageId] ? 'Deleting...' : 'Delete'}
                          </DeleteButton>
                        </ImageActions>
                      </ImageCard>
                    ))}
                  </ImageGrid>
                ) : (
                  <EmptyState>No images found in this path</EmptyState>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'upload' && uploadError && (
          <ErrorMessage>
            <strong>Error:</strong> {uploadError}
          </ErrorMessage>
        )}

        {activeTab === 'upload' && uploadResult && (
          <SuccessMessage>
            <strong>Success!</strong> Image uploaded successfully.
            <ResultInfo>
              <ResultTitle>Upload Details</ResultTitle>
              <ResultItem>
                <strong>Image ID:</strong> {uploadResult.imageId}
              </ResultItem>
              <ResultItem>
                <strong>URL:</strong> <a href={uploadResult.url} target="_blank" rel="noopener noreferrer">{uploadResult.url}</a>
              </ResultItem>
              <ResultItem>
                <strong>Path:</strong> {uploadResult.path}
              </ResultItem>
              {uploadResult.originalSize && (
                <ResultItem>
                  <strong>Original Size:</strong> {(uploadResult.originalSize / 1024).toFixed(2)} KB
                </ResultItem>
              )}
              {uploadResult.compressedSize && (
                <ResultItem>
                  <strong>Compressed Size:</strong> {(uploadResult.compressedSize / 1024).toFixed(2)} KB
                </ResultItem>
              )}
              {uploadResult.compressionRatio && (
                <ResultItem>
                  <strong>Compression Ratio:</strong> {uploadResult.compressionRatio}
                </ResultItem>
              )}
              {uploadResult.url && (
                <ImagePreview src={`${Constants.get.SERVER_URL}${uploadResult.url}`} alt="Uploaded" />
              )}
            </ResultInfo>
          </SuccessMessage>
        )}
      </Card>
    </PageContainer>
  );
}

export default ImageManager;

